import React, { useEffect, useRef, useState } from 'react';
import { Captions, Maximize, Pause, Play, RotateCcw, Volume2, VolumeX } from 'lucide-react';

const MEDIA_BASE = '/assets/marketing/agency-r5';
const timeLabel = seconds => `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60).toString().padStart(2, '0')}`;

export const TrialFilm = () => {
  const video = useRef(null);
  const player = useRef(null);
  const loadedStem = useRef(null);
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 600px)').matches);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [ended, setEnded] = useState(false);
  const [error, setError] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [captions, setCaptions] = useState(false);
  const stem = mobile ? 'agency-r5-mobile' : 'agency-r5';
  useEffect(() => {
    const query = window.matchMedia('(max-width: 600px)');
    const update = e => setMobile(e.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    setError(false); setEnded(false); setProgress(0);
    setDuration(Number.isFinite(v.duration) ? v.duration : 0);
    // The browser selects sources on first mount. Reload only for a real variant change.
    if (loadedStem.current && loadedStem.current !== stem) v.load();
    loadedStem.current = stem;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduced) v.play().catch(() => setPlaying(false));
    return () => { v.pause(); };
  }, [stem]);
  const play = async (fromStart = false, withSound = false) => {
    const v = video.current;
    if (!v) return;
    if (fromStart || ended) v.currentTime = 0;
    if (withSound) { v.muted = false; setMuted(false); }
    try { await v.play(); setEnded(false); }
    catch (_) { setPlaying(false); }
  };
  const toggleAudio = () => {
    if (!video.current) return;
    video.current.muted = !muted; setMuted(!muted);
  };
  const toggleCaptions = () => {
    const track = video.current?.textTracks[0];
    if (!track) return;
    track.mode = captions ? 'hidden' : 'showing'; setCaptions(!captions);
  };
  const fullScreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (player.current?.requestFullscreen) await player.current.requestFullscreen();
      else video.current?.webkitEnterFullscreen?.();
    } catch (_) { /* The native video controls remain available in unsupported browsers. */ }
  };
  return <div className={`trial-film ${mobile ? 'trial-film-mobile' : ''}`} ref={player} data-testid="trial-film-player">
    <video ref={video} className="trial-video" poster={`${MEDIA_BASE}/${stem}.jpg`}
      muted={muted} playsInline preload="metadata" aria-label="The Agency Demo — Voxinity AI voice, chat, and appointment booking"
      data-testid="trial-showcase-video" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
      onLoadedMetadata={() => {
        setDuration(Number.isFinite(video.current?.duration) ? video.current.duration : 0);
        if (video.current?.textTracks[0]) video.current.textTracks[0].mode = captions ? 'showing' : 'hidden';
      }} onCanPlay={() => setError(false)}
      onTimeUpdate={() => setProgress(video.current?.duration ? video.current.currentTime / video.current.duration : 0)}
      onEnded={() => {
        if (video.current?.muted && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) play(true);
        else { setEnded(true); setPlaying(false); }
      }} onError={event => {
        // React propagates source errors here; only the last source below is terminal.
        if (event.target === event.currentTarget) setError(true);
      }}>
      <source src={`${MEDIA_BASE}/${stem}.webm`} type="video/webm" />
      <source src={`${MEDIA_BASE}/${stem}.mp4`} type="video/mp4" onError={() => setError(true)} />
      <track kind="captions" src={`${MEDIA_BASE}/agency-r5.vtt`} srcLang="en" label="English" />
    </video>
    {error ? <div className="trial-film-error" role="alert" data-testid="trial-film-error">The film couldn’t load. <button onClick={() => { setError(false); video.current?.load(); play(); }} data-testid="trial-film-retry">Try again</button></div> : <>
      {muted && !ended && <button type="button" className="trial-film-sound" onClick={() => play(true, true)} data-testid="trial-film-hear-button"><Volume2 size={16} /> Watch with sound</button>}
      {ended && <button type="button" className="trial-film-replay" onClick={() => play(true)} data-testid="trial-film-replay-button"><RotateCcw size={18} /> Watch again</button>}
      <div className="trial-film-controls" data-testid="trial-film-controls">
        <button type="button" title={playing ? 'Pause' : 'Play'} aria-label={playing ? 'Pause film' : 'Play film'} data-testid="trial-film-play-pause" onClick={() => playing ? video.current?.pause() : play()}>{playing ? <Pause size={15} /> : <Play size={15} />}</button>
        <input aria-label="Video progress" type="range" min="0" max="100" step="0.1" value={progress * 100} data-testid="trial-film-seek" onChange={e => { if (video.current?.duration) video.current.currentTime = Number(e.target.value) / 100 * video.current.duration; }} />
        <span data-testid="trial-film-duration">{timeLabel(progress * duration)} / {timeLabel(duration)}</span>
        <button type="button" title={captions ? 'Hide captions' : 'Show captions'} aria-label="English captions" aria-pressed={captions} data-testid="trial-film-captions" onClick={toggleCaptions}><Captions size={16} /></button>
        <button type="button" title={muted ? 'Unmute' : 'Mute'} aria-label={muted ? 'Unmute film' : 'Mute film'} data-testid="trial-film-mute" onClick={toggleAudio}>{muted ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
        <button type="button" title="Fullscreen" aria-label="Fullscreen film" data-testid="trial-film-fullscreen" onClick={fullScreen}><Maximize size={16} /></button>
      </div>
    </>}
  </div>;
};